import type { ReactNode } from 'react';
import { useLocalStorageState } from 'hooks/useLocalStorageState';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { IconChevronDown } from '@tabler/icons-react';
import { cn } from 'helpers/cn';

/**
 * A collapsible wrapper for one report section.
 *
 * The page now carries a validity check, a recommendation, the evaluation controls,
 * significance, power, the stats table and four charts, which is more than anyone reads
 * in one pass. Collapsing lets someone keep the two or three sections they care about
 * open and skip the rest.
 *
 * The open/closed state is persisted per section, not per experiment: the choice is
 * about which parts of the report you personally use, and having it reset each time you
 * opened a different experiment would make it useless.
 *
 * Sections default to open. A first-time visitor should see the whole report rather
 * than a stack of closed headings, and anything worth hiding by default probably should
 * not be on the page.
 */
type ReportSectionProps = {
  /** Stable key for persistence: changing it resets everyone's saved state. */
  id: string;
  title: string;
  /** Shown next to the title when collapsed, so the section is worth finding again. */
  summary?: ReactNode;
  children: ReactNode;
  defaultOpen?: boolean;
  /** Names the section for the product tour, as its `data-tour` anchor. */
  tourAnchor?: string;
};

export const ReportSection = ({ id, title, summary, children, defaultOpen = true, tourAnchor }: ReportSectionProps) => {
  const [open, setOpen] = useLocalStorageState<boolean>(`REPORT_SECTION_${id}`, defaultOpen);

  return (
    <Collapsible open={open} onOpenChange={setOpen} data-tour={tourAnchor}>
      {/* The heading is a rule across the width: it reads as a divider between report
          sections rather than as another card stacked on the panel. */}
      <div className="border-line flex items-center gap-2 border-b pb-1.5">
        <CollapsibleTrigger className="text-dim hover:text-foreground flex items-center gap-1.5 rounded-lg transition-colors duration-150">
          <IconChevronDown
            className={cn('size-3.5 shrink-0 transition-transform duration-150', !open && '-rotate-90')}
            aria-hidden
          />
          <span className="micro-label">{title}</span>
        </CollapsibleTrigger>

        {/* Only useful while closed: open, the section states its own headline. */}
        {!open && summary && <span className="supporting truncate">{summary}</span>}
      </div>

      {/*
        pt on the content rather than a gap on the wrapper: a collapsed section should
        take up only its heading, and a flex gap would still be applied to the hidden
        child.
      */}
      <CollapsibleContent className="pt-4">{children}</CollapsibleContent>
    </Collapsible>
  );
};
